import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Table, Spinner, Row, Col } from "react-bootstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import { IoEyeOutline } from "react-icons/io5";
import { BsCaretUpFill, BsCaretDownFill } from "react-icons/bs";

const AddProductsModal = ({
  show,
  onHide,
  onAddProducts,
  systems = [],
  phases = [],
  optionsSystem = [],
  fetchProductSuggestions,
  selectedProductListIds = [],
  activeSystemId = null,
  activePhaseId = null,
  token,
}) => {
  const [searchProducts, setSearchProducts] = useState("");
  const [selectedSystemId, setSelectedSystemId] = useState(activeSystemId);
  const [selectedPhaseId, setSelectedPhaseId] = useState(activePhaseId);
  const [searchProductsList, setSearchProductsList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "productName",
    direction: "asc",
  });
  const [offset, setOffset] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [searchProductsListTotal, setSearchProductsListTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const requestIdRef = useRef(0);
  const LIMIT = 20;

  const createUniqueKey = (productId, systemId, phaseId) => {
    return `${productId}-${systemId || "null"}-${phaseId || "null"}`;
  };

  const isCombinationSelected = (productId, systemId, phaseId) => {
    const uniqueKey = createUniqueKey(productId, systemId, phaseId);
    return selectedProductListIds.some((item) => {
      const itemKey = createUniqueKey(
        item.productId,
        item.systemId,
        item.phaseId
      );
      return itemKey === uniqueKey;
    });
  };

  const isInCurrentSelection = (productId, systemId, phaseId) => {
    const uniqueKey = createUniqueKey(productId, systemId, phaseId);
    return selectedProducts.some((item) => {
      const itemKey = createUniqueKey(
        item.productId,
        item.systemId,
        item.phaseId
      );
      return itemKey === uniqueKey;
    });
  };

  useEffect(() => {
    if (show) {
      setSelectedSystemId(activeSystemId);
      setSelectedPhaseId(activePhaseId);
      loadInitialProducts();
    }
  }, [show]);

  const loadInitialProducts = async () => {
    setIsSearchLoading(true);
    const data = await fetchProductSuggestions(
      "",
      selectedProductListIds,
      "productName",
      "asc",
      LIMIT,
      0
    );

    const productsWithGlobal = (data?.products || []).map((product) => ({
      ...product,
      systemId: selectedSystemId || product.systemId,
      phaseId: selectedSystemId
        ? selectedPhaseId || product.phaseId
        : product.phaseId,
    }));

    setSearchProductsList(productsWithGlobal);
    setSearchProductsListTotal(data?.total || 0);
    setOffset(data?.offset || 0);
    setProductCount(data?.total || 0);
    setNextOffset(data?.nextOffset || null);
    setIsSearchLoading(false);
  };

  const handleClose = () => {
    setSearchProducts("");
    setSelectedProducts([]);
    setSearchProductsList([]);
    setSelectedSystemId(activeSystemId);
    setSelectedPhaseId(activePhaseId);
    setOffset(0);
    setNextOffset(null);
    setProductCount(0);
    setIsSearchLoading(false);
    setValidationErrors({});
    onHide();
  };

  const handleSort = async (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setIsSearchLoading(true);
    try {
      const data = await fetchProductSuggestions(
        searchProducts,
        selectedProductListIds,
        columnKey,
        direction,
        LIMIT,
        0
      );

      const mergedProducts =
        data?.products?.map((product) => {
          const existingSelected = selectedProducts.find(
            (sel) => sel.productId === product.productId
          );

          if (existingSelected) {
            return {
              ...product,
              systemId: existingSelected.systemId,
              phaseId: existingSelected.phaseId,
              quantity: existingSelected.quantity,
              rate: existingSelected.rate,
              amount: existingSelected.amount,
            };
          }

          return product;
        }) || [];

      setSearchProductsList(mergedProducts);
      setSortConfig({ key: columnKey, direction });
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchProductSuggestions(
        searchProducts,
        selectedProductListIds,
        sortConfig.key,
        sortConfig.direction,
        LIMIT,
        nextOffset
      );

      if (data?.products && data.products.length > 0) {
        const mergedNewProducts = data.products.map((product) => {
          const existingSelected = selectedProducts.find(
            (sel) => sel.productId === product.productId
          );

          if (existingSelected) {
            return {
              ...product,
              systemId: existingSelected.systemId,
              phaseId: existingSelected.phaseId,
              quantity: existingSelected.quantity,
              rate: existingSelected.rate,
              amount: existingSelected.amount,
            };
          }
          return product;
        });

        setSearchProductsList((prev) => [...prev, ...mergedNewProducts]);
        setNextOffset(data.nextOffset);
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  const getFilteredPhasesForSystem = (systemId) => {
    if (!systemId) return [];
    const selectedSystem = systems.find((sys) => sys.system_id === systemId);
    if (!selectedSystem || !selectedSystem.phases) return [];
    return selectedSystem.phases.map((phase) => ({
      value: phase.phase_id,
      label: phase.phase_name,
    }));
  };

  const [itemViewModel, setItemViewModel] = useState(false);
  const [viewedItem, setViewedItem] = useState(null);

  const handleProductView = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setViewedItem(item);
    setItemViewModel(true);
  };

  const handleCloseView = () => {
    setItemViewModel(false);
    setViewedItem(null);
  };

  const validateSelectedProducts = () => {
    const errors = {};
    let hasErrors = false;

    selectedProducts.forEach((item, index) => {
      const uniqueKey = createUniqueKey(
        item.productId,
        item.systemId,
        item.phaseId
      );
      errors[uniqueKey] = {};

      if (!item.quantity || item.quantity <= 0) {
        errors[uniqueKey].quantity = "Quantity is required";
        hasErrors = true;
      }

      if (
        item.rate === "" ||
        item.rate === null ||
        item.rate === undefined ||
        parseFloat(item.rate) <= 0
      ) {
        errors[uniqueKey].rate = "Rate must be greater than 0";
        hasErrors = true;
      }

      if (!item.systemId) {
        errors[uniqueKey].systemId = "System is required";
        hasErrors = true;
      }

      if (!item.phaseId) {
        errors[uniqueKey].phaseId = "Phase is required";
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleAddClick = () => {
    if (!validateSelectedProducts()) {
      return;
    }

    const newItems = selectedProducts
      .filter((selected) => {
        return !isCombinationSelected(
          selected.productId,
          selected.systemId,
          selected.phaseId
        );
      })
      .map((item) => {
        const calculatedAmount =
          item.rate && item.quantity
            ? (parseFloat(item.rate) * parseFloat(item.quantity)).toFixed(2)
            : "0.00";

        return {
          ...item,
          amount: calculatedAmount,
        };
      });

    onAddProducts(newItems);
    handleClose();
  };

  const getAvailableProducts = () => {
    return searchProductsList.filter((product) => {
      return !isCombinationSelected(
        product.productId,
        product.systemId,
        product.phaseId
      );
    });
  };

  const availableProducts = getAvailableProducts();

  return (
    <>
      <Modal
        show={itemViewModel}
        onHide={handleCloseView}
        backdrop="static"
        keyboard={true}
        size="xl"
        height="90vh"
        style={{ zIndex: "9999999" }}
      >
        <Modal.Header className="custom-modal-header">
          <Modal.Title>View Product</Modal.Title>
          <button
            type="button"
            className="btn-close-svg"
            onClick={handleCloseView}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body>
          {viewedItem && (
            <>
              <Row>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Product Name</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.productName || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Make</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.manufacturerName || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Model</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.model || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>SKU</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.sku || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                
                  
                
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.quantity || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Rate</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.rate || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={viewedItem.amount || ""}
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm="12">
                  <Form.Group className="form-group">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      readOnly
                      as="textarea"
                      value={viewedItem.description || ""}
                      style={{
                        resize: "none",
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>System</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={
                        optionsSystem.find(
                          (option) => option.value === viewedItem.systemId
                        )?.label || "N/A"
                      }
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group className="form-group">
                    <Form.Label>Phase</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={
                        viewedItem.systemId && viewedItem.phaseId
                          ? getFilteredPhasesForSystem(
                            viewedItem.systemId
                          ).find(
                            (option) => option.value === viewedItem.phaseId
                          )?.label || "N/A"
                          : "N/A"
                      }
                      style={{
                        fontSize: "0.8rem",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={true}
        size="xl"
        className="custom-modal-pr-width"
        style={{ zIndex: "9999" }}
      >
        <Modal.Header className="custom-modal-header">
          <Modal.Title>Select products</Modal.Title>
          <button
            type="button"
            className="btn-close-svg"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body
          style={{
            padding: "1rem",
            position: "relative",
          }}
        >
          <Row className="mb-6">
            <Col md={4} sm={12} className="mt-0 mt-md-0">
              <Form.Control
                type="text"
                name="name"
                className="w-100"
                placeholder="Search product"
                value={searchProducts}
                onChange={async (e) => {
                  const inputValue = e.target.value;
                  setSearchProducts(inputValue);
                  const currentRequestId = ++requestIdRef.current;

                  setIsSearchLoading(true);
                  try {
                    const data = await fetchProductSuggestions(
                      inputValue,
                      selectedProductListIds,
                      sortConfig.key,
                      sortConfig.direction,
                      LIMIT,
                      0
                    );
                    if (requestIdRef.current === currentRequestId) {
                      const mergedProducts =
                        data?.products?.map((product) => {
                          const existingSelected = selectedProducts.find(
                            (sel) => sel.productId === product.productId
                          );

                          if (existingSelected) {
                            return {
                              ...product,
                              systemId: existingSelected.systemId,
                              phaseId: existingSelected.phaseId,
                              quantity: existingSelected.quantity,
                              rate: existingSelected.rate,
                              amount: existingSelected.amount,
                            };
                          }

                          if (selectedSystemId || selectedPhaseId) {
                            return {
                              ...product,
                              systemId: selectedSystemId || product.systemId,
                              phaseId: selectedSystemId
                                ? selectedPhaseId || product.phaseId
                                : product.phaseId,
                            };
                          }
                          return product;
                        }) || [];

                      setSearchProductsList(mergedProducts);
                      setSearchProductsListTotal(data?.total || 0);
                      setOffset(data?.offset || 0);
                      setProductCount(data?.total || 0);
                      setNextOffset(data?.nextOffset || null);
                      setIsSearchLoading(false);
                    }
                  } catch (error) {
                    if (requestIdRef.current === currentRequestId) {
                      setSearchProductsList([]);
                      setOffset(0);
                      setProductCount(0);
                      setNextOffset(null);
                      setIsSearchLoading(false);
                    }
                  }
                }}
              />
            </Col>
            <Col md={4} sm={12} className="mt-4 mt-md-0">
              <Select
                closeMenuOnScroll={(e) => {
                  try {
                    const target = e.target;

                    if (
                      !target ||
                      !target.nodeType ||
                      typeof target.closest !== "function"
                    ) {
                      return true;
                    }

                    const isMenuScroll =
                      target.closest('[class*="MenuList"]') ||
                      target.closest('[class*="menu"]');

                    return !isMenuScroll;
                  } catch (error) {
                    return true;
                  }
                }}
                name="systemId"
                options={optionsSystem}
                value={optionsSystem.find(
                  (option) => option.value === selectedSystemId
                )}
                onChange={(selectedOption) => {
                  const newSystemId = selectedOption?.value || null;
                  setSelectedSystemId(newSystemId);
                  setSelectedPhaseId(null);
                  const updatedSearchList = searchProductsList?.map(
                    (product) => {
                      const isProductSelected = isInCurrentSelection(
                        product.productId,
                        product.systemId,
                        product.phaseId
                      );

                      if (isProductSelected && product.systemId !== null) {
                        return product;
                      }

                      return {
                        ...product,
                        systemId: newSystemId,
                        phaseId: null,
                      };
                    }
                  );
                  setSearchProductsList(updatedSearchList);

                  const updatedSelectedList = selectedProducts?.map(
                    (product) => {
                      if (product.systemId !== null) {
                        return product;
                      }

                      return {
                        ...product,
                        systemId: newSystemId,
                        phaseId: null,
                      };
                    }
                  );
                  setSelectedProducts(updatedSelectedList);
                }}
                isClearable
                placeholder="Select System"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={{
                  container: (provided) => ({ ...provided, width: "100%" }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#007bff"
                      : state.isFocused
                        ? "#e6f7ff"
                        : "#fff",
                    color: "#333",
                    fontSize: "0.8rem",
                  }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  control: (provided) => ({ ...provided, color: "#5e667b" }),
                  placeholder: (provided) => ({
                    ...provided,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
                }}
              />
            </Col>
            <Col md={4} sm={12} className="mt-4 mt-md-0 mb-3 mb-md-0">
              <Select
                closeMenuOnScroll={(e) => {
                  try {
                    const target = e.target;

                    if (
                      !target ||
                      !target.nodeType ||
                      typeof target.closest !== "function"
                    ) {
                      return true;
                    }

                    const isMenuScroll =
                      target.closest('[class*="MenuList"]') ||
                      target.closest('[class*="menu"]');

                    return !isMenuScroll;
                  } catch (error) {
                    return true;
                  }
                }}
                name="phaseId"
                options={getFilteredPhasesForSystem(selectedSystemId)}
                value={
                  selectedSystemId && selectedPhaseId
                    ? getFilteredPhasesForSystem(selectedSystemId).find(
                      (option) => option.value === selectedPhaseId
                    )
                    : null
                }
                onChange={(selectedOption) => {
                  const newPhaseId = selectedOption?.value || null;
                  setSelectedPhaseId(newPhaseId);

                  const updatedSearchList = searchProductsList?.map(
                    (product) => {
                      const isProductSelected = isInCurrentSelection(
                        product.productId,
                        product.systemId,
                        product.phaseId
                      );

                      if (isProductSelected && product.phaseId !== null) {
                        return product;
                      }

                      return {
                        ...product,
                        phaseId: newPhaseId,
                      };
                    }
                  );
                  setSearchProductsList(updatedSearchList);

                  const updatedSelectedList = selectedProducts?.map(
                    (product) => {
                      if (product.phaseId !== null) {
                        return product;
                      }

                      return {
                        ...product,
                        phaseId: newPhaseId,
                      };
                    }
                  );
                  setSelectedProducts(updatedSelectedList);
                }}
                isClearable
                isDisabled={!selectedSystemId}
                placeholder={
                  selectedSystemId ? "Select phase" : "First select a system"
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                styles={{
                  container: (provided) => ({ ...provided, width: "100%" }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#007bff"
                      : state.isFocused
                        ? "#e6f7ff"
                        : "#fff",
                    color: "#333",
                    fontSize: "0.8rem",
                  }),
                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  control: (provided) => ({
                    ...provided,
                    color: "#5e667b",
                    backgroundColor: !selectedSystemId ? "#e9ecef" : "#fff",
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
                }}
              />
            </Col>
          </Row>

          <div
            style={{
              maxHeight: "calc(75vh - 150px)",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <Table
              id="bom-list-table datatable"
              className="overflow-auto mt-6"
              role="grid"
              responsive
              data-toggle="data-table"
              style={{ width: "100%", marginTop: "1rem", overflow: "auto" }}
            >
              <thead
                className="sticky-top"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "#e2e4e9",
                }}
              >
                <tr style={{ backgroundColor: "#e2e4e9" }}>
                  <th style={{ width: "200px", textAlign: "center" }}>
                    <Form.Check
                      type="checkbox"
                      checked={
                        availableProducts.length > 0 &&
                        !isSearchLoading &&
                        availableProducts.every((product) =>
                          isInCurrentSelection(
                            product.productId,
                            product.systemId,
                            product.phaseId
                          )
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          const newSelections = availableProducts.filter(
                            (product) =>
                              !isInCurrentSelection(
                                product.productId,
                                product.systemId,
                                product.phaseId
                              )
                          );
                          setSelectedProducts([
                            ...selectedProducts,
                            ...newSelections,
                          ]);
                        } else {
                          const keysToDeselect = availableProducts.map(
                            (product) =>
                              createUniqueKey(
                                product.productId,
                                product.systemId,
                                product.phaseId
                              )
                          );
                          setSelectedProducts(
                            selectedProducts.filter(
                              (selected) =>
                                !keysToDeselect.includes(
                                  createUniqueKey(
                                    selected.productId,
                                    selected.systemId,
                                    selected.phaseId
                                  )
                                )
                            )
                          );
                        }
                      }}
                    />
                  </th>
                  <th
                    onClick={() =>
                      !isSearchLoading && handleSort("productName")
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex flex-row align-items-end user-select-none">
                      Product Name{" "}
                      <span className="d-flex flex-column ms-2">
                        <BsCaretUpFill
                          color={
                            sortConfig.key === "productName" &&
                              sortConfig.direction === "asc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                        <BsCaretDownFill
                          color={
                            sortConfig.key === "productName" &&
                              sortConfig.direction === "desc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                      </span>
                    </div>
                  </th>
                  <th style={{ minWidth: "100px" }}>Quantity</th>
                  <th
                    onClick={() =>
                      !isSearchLoading && handleSort("manufacturerName")
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex flex-row align-items-end user-select-none">
                      Make{" "}
                      <span className="d-flex flex-column ms-2">
                        <BsCaretUpFill
                          color={
                            sortConfig.key === "manufacturerName" &&
                              sortConfig.direction === "asc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                        <BsCaretDownFill
                          color={
                            sortConfig.key === "manufacturerName" &&
                              sortConfig.direction === "desc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                      </span>
                    </div>
                  </th>
                  <th
                    onClick={() => !isSearchLoading && handleSort("model")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex flex-row align-items-end user-select-none">
                      Model{" "}
                      <span className="d-flex flex-column ms-2">
                        <BsCaretUpFill
                          color={
                            sortConfig.key === "model" &&
                              sortConfig.direction === "asc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                        <BsCaretDownFill
                          color={
                            sortConfig.key === "model" &&
                              sortConfig.direction === "desc"
                              ? "#3a57e8"
                              : "#ccc"
                          }
                        />
                      </span>
                    </div>
                  </th>
                  <th style={{ minWidth: "200px" }}>SKU</th>
                  
                  <th style={{ minWidth: "200px" }}>Rate</th>
                  <th style={{ minWidth: "200px" }}>Amount</th>
                  <th style={{ minWidth: "200px" }}>System</th>
                  <th style={{ minWidth: "200px" }}>Phase</th>
                  <th style={{ minWidth: "100px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!isSearchLoading &&
                  availableProducts.length > 0 &&
                  availableProducts.map((item, index) => {
                    const isSelected = isInCurrentSelection(
                      item.productId,
                      item.systemId,
                      item.phaseId
                    );
                    const uniqueKey = createUniqueKey(
                      item.productId,
                      item.systemId,
                      item.phaseId
                    );
                    const itemErrors = validationErrors[uniqueKey] || {};
                    const hasError = Object.keys(itemErrors).length > 0;

                    return (
                      <tr
                        key={uniqueKey}
                        style={{
                          ...(isSelected && { backgroundColor: "#ecf2f9c2" }),
                          ...(hasError &&
                            isSelected && { border: "2px solid red" }),
                        }}
                        onClick={(e) => {
                          if (
                            e.target.tagName === "TD" ||
                            e.target.type === "checkbox"
                          ) {
                            if (isSelected) {
                              setSelectedProducts(
                                selectedProducts.filter(
                                  (i) =>
                                    createUniqueKey(
                                      i.productId,
                                      i.systemId,
                                      i.phaseId
                                    ) !== uniqueKey
                                )
                              );
                              const newErrors = { ...validationErrors };
                              delete newErrors[uniqueKey];
                              setValidationErrors(newErrors);
                            } else {
                              setSelectedProducts([...selectedProducts, item]);
                            }
                          }
                        }}
                      >
                        <td style={{ textAlign: "center" }}>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setSelectedProducts(
                                  selectedProducts.filter(
                                    (i) =>
                                      createUniqueKey(
                                        i.productId,
                                        i.systemId,
                                        i.phaseId
                                      ) !== uniqueKey
                                  )
                                );
                                const newErrors = { ...validationErrors };
                                delete newErrors[uniqueKey];
                                setValidationErrors(newErrors);
                              } else {
                                setSelectedProducts([
                                  ...selectedProducts,
                                  item,
                                ]);
                              }
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            readOnly
                            type="text"
                            value={item.productName || ""}
                            style={{
                              width: "200px",
                              fontSize: "0.8rem",
                              cursor: "not-allowed",
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={item.quantity || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                const updatedList = searchProductsList?.map(
                                  (product) => {
                                    const productKey = createUniqueKey(
                                      product.productId,
                                      product.systemId,
                                      product.phaseId
                                    );
                                    return productKey === uniqueKey
                                      ? {
                                        ...product,
                                        amount: "",
                                        quantity: "",
                                      }
                                      : product;
                                  }
                                );
                                setSearchProductsList(updatedList);
                                const updatedSelectedList =
                                  selectedProducts?.map((product) => {
                                    const productKey = createUniqueKey(
                                      product.productId,
                                      product.systemId,
                                      product.phaseId
                                    );
                                    return productKey === uniqueKey
                                      ? {
                                        ...product,
                                        amount: "",
                                        quantity: "",
                                      }
                                      : product;
                                  });
                                setSelectedProducts(updatedSelectedList);
                                return;
                              }

                              if (!/^\d+$/.test(value)) {
                                return;
                              }

                              const parsedValue = parseInt(value, 10);
                              if (parsedValue > 99999) {
                                return;
                              }

                              if (!isNaN(parsedValue) && parsedValue >= 0) {
                                const updatedAmount = (
                                  parsedValue * (item.rate || 0)
                                ).toFixed(2);

                                const updatedList = searchProductsList?.map(
                                  (product) => {
                                    const productKey = createUniqueKey(
                                      product.productId,
                                      product.systemId,
                                      product.phaseId
                                    );
                                    return productKey === uniqueKey
                                      ? {
                                        ...product,
                                        amount: updatedAmount,
                                        quantity: parsedValue,
                                      }
                                      : product;
                                  }
                                );
                                setSearchProductsList(updatedList);

                                const updatedSelectedList =
                                  selectedProducts?.map((product) => {
                                    const productKey = createUniqueKey(
                                      product.productId,
                                      product.systemId,
                                      product.phaseId
                                    );
                                    return productKey === uniqueKey
                                      ? {
                                        ...product,
                                        amount: updatedAmount,
                                        quantity: parsedValue,
                                      }
                                      : product;
                                  });
                                setSelectedProducts(updatedSelectedList);
                              }
                            }}
                            style={{ width: "100px", fontSize: "0.8rem" }}
                          />
                          {itemErrors.quantity && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {itemErrors.quantity}
                            </div>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            readOnly
                            type="text"
                            value={item.manufacturerName || ""}
                            style={{
                              width: "200px",
                              fontSize: "0.8rem",
                              cursor: "not-allowed",
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            readOnly
                            type="text"
                            value={item.model || ""}
                            style={{
                              width: "auto",
                              fontSize: "0.8rem",
                              cursor: "not-allowed",
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            readOnly
                            type="text"
                            value={item.sku || ""}
                            style={{
                              width: "200px",
                              fontSize: "0.8rem",
                              cursor: "not-allowed",
                            }}
                          />
                        </td>
                    
                        <td>
                          <Form.Control
                            type="text"
                            step="0.01"
                            min={0}
                            value={item.rate ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              const selectedProductId = item?.productId;

                              if (value === "") {
                                const updatedData = searchProductsList.map((p) =>
                                  p.productId === selectedProductId
                                    ? { ...p, rate: "", amount: "" }
                                    : p
                                );
                                setSearchProductsList(updatedData);

                                const selectedProductIdx = selectedProducts.findIndex(
                                  (p) => p.productId === selectedProductId
                                );
                                if (selectedProductIdx !== -1) {
                                  const updatedSelected = [...selectedProducts];
                                  updatedSelected[selectedProductIdx] = {
                                    ...updatedSelected[selectedProductIdx],
                                    rate: "",
                                    amount: "",
                                  };
                                  setSelectedProducts(updatedSelected);
                                }
                                return;
                              }

                              if (/^\d*\.?\d{0,2}$/.test(value)) {
                                const [integerPart] = value.split(".");

                                if (integerPart && integerPart.length > 13) {
                                  return;
                                }

                                const numericValue = parseFloat(value);

                                if (numericValue > 9999999999999.99) {
                                  return;
                                }

                                const updatedAmount = (
                                  (parseFloat(value) || 0) * (item.quantity || 0)
                                ).toFixed(2);

                                const updatedData = searchProductsList.map((p) =>
                                  p.productId === selectedProductId
                                    ? {
                                      ...p,
                                      rate: value,
                                      amount: updatedAmount,
                                    }
                                    : p
                                );
                                setSearchProductsList(updatedData);

                                const selectedProductIdx = selectedProducts.findIndex(
                                  (p) => p.productId === selectedProductId
                                );
                                if (selectedProductIdx !== -1) {
                                  const updatedSelected = [...selectedProducts];
                                  updatedSelected[selectedProductIdx] = {
                                    ...updatedSelected[selectedProductIdx],
                                    rate: value,
                                    amount: updatedAmount,
                                  };
                                  setSelectedProducts(updatedSelected);
                                }
                              }
                            }}
                            style={{
                              width: "200px",
                              fontSize: "0.8rem",
                            }}
                          />
                          {itemErrors.rate && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {itemErrors.rate}
                            </div>
                          )}
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            readOnly
                            value={item.amount || 0}
                            style={{
                              width: "200px",
                              fontSize: "0.8rem",
                              cursor: "not-allowed",
                            }}
                          />
                        </td>
                        <td>
                          <Select
                            closeMenuOnScroll={(e) => {
                              try {
                                const target = e.target;

                                if (
                                  !target ||
                                  !target.nodeType ||
                                  typeof target.closest !== "function"
                                ) {
                                  return true;
                                }

                                const isMenuScroll =
                                  target.closest('[class*="MenuList"]') ||
                                  target.closest('[class*="menu"]');

                                return !isMenuScroll;
                              } catch (error) {
                                return true;
                              }
                            }}
                            options={optionsSystem}
                            value={optionsSystem.find(
                              (option) => option.value === item.systemId
                            )}
                            onChange={(selectedOption) => {
                              const newSystemId = selectedOption?.value || null;

                              const updatedList = searchProductsList?.map(
                                (product) => {
                                  const productKey = createUniqueKey(
                                    product.productId,
                                    product.systemId,
                                    product.phaseId
                                  );
                                  return productKey === uniqueKey
                                    ? {
                                      ...product,
                                      systemId: newSystemId,
                                      phaseId: null,
                                    }
                                    : product;
                                }
                              );
                              setSearchProductsList(updatedList);

                              const updatedSelectedList = selectedProducts?.map(
                                (product) => {
                                  const productKey = createUniqueKey(
                                    product.productId,
                                    product.systemId,
                                    product.phaseId
                                  );
                                  return productKey === uniqueKey
                                    ? {
                                      ...product,
                                      systemId: newSystemId,
                                      phaseId: null,
                                    }
                                    : product;
                                }
                              );
                              setSelectedProducts(updatedSelectedList);
                            }}
                            isClearable
                            placeholder="Select System"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "250px",
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected
                                  ? "#007bff"
                                  : state.isFocused
                                    ? "#e6f7ff"
                                    : "#fff",
                                color: "#333",
                                fontSize: "0.8rem",
                              }),
                              menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              control: (provided) => ({
                                ...provided,
                                color: "#5e667b",
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }),
                            }}
                          />
                          {itemErrors.systemId && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {itemErrors.systemId}
                            </div>
                          )}
                        </td>
                        <td>
                          <Select
                            closeMenuOnScroll={(e) => {
                              try {
                                const target = e.target;

                                if (
                                  !target ||
                                  !target.nodeType ||
                                  typeof target.closest !== "function"
                                ) {
                                  return true;
                                }

                                const isMenuScroll =
                                  target.closest('[class*="MenuList"]') ||
                                  target.closest('[class*="menu"]');

                                return !isMenuScroll;
                              } catch (error) {
                                return true;
                              }
                            }}
                            options={getFilteredPhasesForSystem(item.systemId)}
                            value={
                              item.systemId && item.phaseId
                                ? getFilteredPhasesForSystem(
                                  item.systemId
                                ).find(
                                  (option) => option.value === item.phaseId
                                )
                                : null
                            }
                            onChange={(selectedOption) => {
                              const newPhaseId = selectedOption?.value || null;

                              const updatedList = searchProductsList?.map(
                                (product) => {
                                  const productKey = createUniqueKey(
                                    product.productId,
                                    product.systemId,
                                    product.phaseId
                                  );
                                  return productKey === uniqueKey
                                    ? { ...product, phaseId: newPhaseId }
                                    : product;
                                }
                              );
                              setSearchProductsList(updatedList);

                              const updatedSelectedList = selectedProducts?.map(
                                (product) => {
                                  const productKey = createUniqueKey(
                                    product.productId,
                                    product.systemId,
                                    product.phaseId
                                  );
                                  return productKey === uniqueKey
                                    ? { ...product, phaseId: newPhaseId }
                                    : product;
                                }
                              );
                              setSelectedProducts(updatedSelectedList);
                            }}
                            isClearable
                            placeholder={
                              item.systemId
                                ? "Select phase"
                                : "First select a system"
                            }
                            isDisabled={!item.systemId}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            styles={{
                              container: (provided) => ({
                                ...provided,
                                width: "280px",
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected
                                  ? "#007bff"
                                  : state.isFocused
                                    ? "#e6f7ff"
                                    : "#fff",
                                color: "#333",
                                fontSize: "0.8rem",
                              }),
                              menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                              control: (provided) => ({
                                ...provided,
                                color: "#5e667b",
                                backgroundColor: !item.systemId
                                  ? "#e9ecef"
                                  : "#fff",
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }),
                            }}
                          />
                          {itemErrors.phaseId && (
                            <div
                              className="text-danger"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {itemErrors.phaseId}
                            </div>
                          )}
                        </td>
                        <td>
                          <Link
                            className="btn btn-sm btn-icon btn-success"
                            onClick={(e) => handleProductView(e, item)}
                            title="View"
                          >
                            <span className="btn-inner">
                              <IoEyeOutline
                                style={{ width: "20px", height: "20px" }}
                              />
                            </span>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                <tr>
                  {(!isSearchLoading || isLoadingMore) &&
                    nextOffset !== null &&
                    nextOffset <= productCount - 1 && (
                      <div
                        style={{
                          position: "sticky",
                          bottom: "0",
                          left: "45%",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          backgroundColor: "white",
                          padding: "1rem 0",
                          zIndex: 10,
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-lg px-4 py-2"
                          onClick={handleLoadMore}
                          disabled={isLoadingMore}
                          style={{
                            borderRadius: "20px",
                            fontWeight: "400",
                            fontSize: "14px",
                            minWidth: "150px",
                          }}
                        >
                          {isLoadingMore ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-plus-circle me-2" />
                              Load More
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  {isSearchLoading && !isLoadingMore && (
                    <div
                      style={{
                        position: "sticky",
                        bottom: "0",
                        left: "45%",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        backgroundColor: "white",
                        padding: "1rem 0",
                        zIndex: 10,
                      }}
                    >
                      <div className="d-flex flex-column align-items-center">
                        <Spinner
                          animation="border"
                          role="status"
                          variant="primary"
                          size="lg"
                          style={{ width: "3rem", height: "3rem" }}
                        />
                        <div className="mt-3 text-muted">
                          <h5>Searching products...</h5>
                          <p className="mb-0">
                            Please wait while we fetch the results
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isSearchLoading &&
                    !isLoadingMore &&
                    availableProducts.length < 1 && (
                      <div
                        style={{
                          position: "sticky",
                          left: "40%",
                          width: "100%",
                          padding: "3rem 0",
                          zIndex: 10,
                        }}
                      >
                        <div className="d-flex flex-column align-items-center text-muted">
                          <h5>
                            {searchProductsListTotal < 1 && "No products found"}
                          </h5>
                          {searchProducts.trim().length > 0 && (
                            <p className="mb-0">
                              Try adjusting your search criteria
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                </tr>
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <Button
            variant="primary"
            disabled={selectedProducts.length === 0}
            onClick={handleAddClick}
          >
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default AddProductsModal;
